import { Separator, Skeleton } from '@bpinternal/ui-kit-next'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import z from 'zod'
import { BannerPortal, Boundary, Page } from '~/components'
import { BillingReadonlyCallout } from '~/features/billing/components'
import { AddonsSection } from '~/features/billing/components/AddonsSection'
import { AiSpendSection } from '~/features/billing/components/AiSpendSection'
import { BillingInformationSection } from '~/features/billing/components/BillingInformationSection'
import { FreeTrialBannerWrapper } from '~/features/billing/components/FreeTrialBanner'
import { PlanBanner } from '~/features/billing/components/PlanBanner'
import { PromoCodeSection } from '~/features/billing/components/PromoCodeSection'
import { SaleEventBannerWrapper } from '~/features/billing/components/SaleEventBanner'
import { UpcomingPaymentSummary } from '~/features/billing/components/UpcomingPaymentSummary'
import { showWelcomeDialog } from '~/features/billing/components/WelcomeDialog'
import { isAuthorized } from '~/features/users/services/permissions'
import { SidebarLayout } from '~/layouts'
import { getQueryOptions } from '~/services'

const billingSearchSchema = z.object({
  newPlan: z.string().optional(),
})

export const Route = createFileRoute('/workspaces/$workspaceId/billing')({
  beforeLoad: async ({ context, params }) => {
    const members = await context.queryClient.ensureQueryData(
      getQueryOptions('workspaces_/$workspaceId_/members', { workspaceId: params.workspaceId })
    )

    const workspaceMember = members?.find((member) => member.userId === context.user.id)

    if (
      !workspaceMember ||
      !isAuthorized({
        permission: 'workspace.manageBilling',
        role: workspaceMember.role,
      })
    ) {
      throw redirect({
        to: '/forbidden',
        search: { workspaceId: params.workspaceId },
      })
    }
  },
  loader: async ({ context, params }) => {
    const billingReadonly = await context.queryClient.ensureQueryData(
      getQueryOptions('workspaces_/$workspaceId_/billing/readonly', { workspaceId: params.workspaceId })
    )

    return { billingReadonly }
  },
  component: Component,
  validateSearch: billingSearchSchema,
})

function Component() {
  const { workspaceId } = Route.useParams()
  const { billingReadonly } = Route.useLoaderData()
  const { newPlan } = Route.useSearch()
  const navigate = Route.useNavigate()

  useEffect(() => {
    if (newPlan === 'plus' || newPlan === 'team') {
      showWelcomeDialog({ planName: newPlan, workspaceId })
      navigate({
        from: Route.fullPath,
        to: Route.fullPath,
        replace: true,
        search: (prev) => ({
          ...prev,
          newPlan: undefined,
        }),
      })
    }
  }, [newPlan, workspaceId])

  return (
    <Page title="Billing">
      <BannerPortal>
        <Boundary onError={() => null} suspenseFallback={null}>
          <FreeTrialBannerWrapper workspaceId={workspaceId} />
        </Boundary>
        {billingReadonly && <BillingReadonlyCallout />}
        <Boundary onError={() => null} suspenseFallback={null}>
          <SaleEventBannerWrapper workspaceId={workspaceId} />
        </Boundary>
      </BannerPortal>
      <SidebarLayout
        main={
          <div className="flex flex-col gap-12 sm:gap-14">
            <div className="lg:hidden">
              <Boundary suspenseFallback={<Skeleton className="h-96 w-full rounded-xl" />} onError={() => null}>
                <UpcomingPaymentSummary workspaceId={workspaceId} />
              </Boundary>
            </div>
            <Boundary suspenseFallback={<Skeleton className="h-[140px] w-full rounded-xl" />} onError={() => null}>
              <PlanBanner workspaceId={workspaceId} />
            </Boundary>

            <Boundary suspenseFallback={<Skeleton className="h-[188px] w-full rounded-xl" />} onError={() => null}>
              <AiSpendSection workspaceId={workspaceId} />
            </Boundary>
            <Separator />

            <Boundary suspenseFallback={<Skeleton className="h-[556px] w-full rounded-xl" />} onError={() => null}>
              <AddonsSection workspaceId={workspaceId} />
            </Boundary>

            <Separator />
            <BillingInformationSection workspaceId={workspaceId} />
            <Separator />
            <PromoCodeSection workspaceId={workspaceId} />
            <Separator />
            <span className="text-sm text-muted-foreground">
              Need help with billing? Contact support or reach out to{' '}
              <a className="underline" href="mailto:billing@botpress.com">
                billing@botpress.com
              </a>
            </span>
          </div>
        }
        containerClassName="lg:gap-5"
        rightSidebarClassName={'sm:w-[363px]'}
        rightSidebarSize="4"
        rightSidebar={
          <div className="sticky top-20">
            <div className="h-[644px] w-[363px]">
              <Boundary suspenseFallback={<Skeleton className="h-full w-full rounded-xl" />} onError={() => null}>
                <UpcomingPaymentSummary workspaceId={workspaceId} />
              </Boundary>
            </div>
          </div>
        }
      />
    </Page>
  )
}
